import { useEffect, useState, type ReactNode } from 'react'
import { Button } from '../../components/Button'
import { Modal } from '../../components/Modal'
import { useAppData } from '../../context/AppDataContext'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import type { AccountStatus, Engagement } from '../../data/types'
import { Select } from '../../components/Select'

/** The three tabs on the Clients page, which is what the form opens on. */
export type ClientKind = 'recurring' | 'one_off' | 'prospect'

const accentColors = ['#FC2265', '#3b7dde', '#2f9e57', '#d98e1c', '#8a5cf5', '#e0433a', '#0ea5a3', '#fd5c86']
const statuses: AccountStatus[] = ['Active', 'Unconfirmed', 'Prospect', 'Former']

function startingPoint(kind: ClientKind): { status: AccountStatus; engagement: Engagement } {
  if (kind === 'prospect') return { status: 'Prospect', engagement: 'Recurring' }
  return { status: 'Active', engagement: kind === 'one_off' ? 'One-off' : 'Recurring' }
}

export function AddClientModal({
  open,
  onClose,
  kind = 'recurring',
}: {
  open: boolean
  onClose: () => void
  kind?: ClientKind
}) {
  const { clients, addClient } = useAppData()
  const { showToast } = useToast()
  // What a client pays is an Admin's to set; a manager adds the client without it.
  const seesMoney = useAuth().hasRole('PARTNER')
  const [name, setName] = useState('')
  const [industry, setIndustry] = useState('')
  const [status, setStatus] = useState<AccountStatus>(startingPoint(kind).status)
  const [engagement, setEngagement] = useState<Engagement>(startingPoint(kind).engagement)
  const [retainer, setRetainer] = useState('')

  // Opened from a tab, the form starts as that tab's kind of client.
  useEffect(() => {
    if (!open) return
    const start = startingPoint(kind)
    setStatus(start.status)
    setEngagement(start.engagement)
  }, [open, kind])

  function reset() {
    setName('')
    setIndustry('')
    setRetainer('')
  }

  async function handleCreate() {
    if (!name.trim()) return
    const initials = name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
    const trimmedName = name.trim()
    const amount = Number(retainer)
    await addClient({
      name: trimmedName,
      initials,
      color: accentColors[clients.length % accentColors.length],
      industry: industry.trim(),
      accountStatus: status,
      engagement,
      // Only a monthly arrangement has a retainer, and only a number typed in
      // is one: an empty box is "not on file", never a retainer of zero.
      ...(engagement === 'Recurring' && seesMoney && retainer.trim() !== '' && Number.isFinite(amount)
        ? { monthlyRetainer: amount }
        : {}),
    })
    showToast(`${trimmedName} added to ${status === 'Prospect' ? 'Prospects' : engagement === 'One-off' ? 'One-off clients' : 'Recurring clients'}.`)
    reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={kind === 'prospect' ? 'Add prospect' : 'Add client'}
      description={
        kind === 'prospect'
          ? 'Somebody who has not signed up yet. Tasks can be filed against them all the same.'
          : 'Someone the studio works for. Their page, month and documents start from here.'
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!name.trim()}>
            {kind === 'prospect' ? 'Add prospect' : 'Add client'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Dr. Megha's Clinic"
            className="field-input"
          />
        </Field>
        <Field label="What they do">
          <input
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="e.g. Dermatology"
            className="field-input"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="How they pay">
            <Select
              value={engagement}
              onChange={(e) => setEngagement(e.target.value as Engagement)}
              className="field-input"
            >
              <option value="Recurring">Recurring, every month</option>
              <option value="One-off">One-off job</option>
            </Select>
          </Field>
          <Field label="Status">
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as AccountStatus)}
              className="field-input"
            >
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        {engagement === 'Recurring' && seesMoney && (
          <Field label="Monthly retainer (INR)">
            <input
              type="number"
              inputMode="numeric"
              min={0}
              value={retainer}
              onChange={(e) => setRetainer(e.target.value)}
              placeholder="Leave empty if not agreed yet"
              className="field-input"
            />
          </Field>
        )}
      </div>
    </Modal>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {children}
    </div>
  )
}
